import { useState, useEffect, useMemo, useCallback } from "react";
import { useFactory } from "@/core/hooks/useFactory";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePushNotifications } from "@/core/hooks/usePushNotifications";
import { useLanguage } from "@/core/LanguageContext";
import { useAuth } from "@/core/AuthContext";
import { useUserFactories } from "@/core/hooks/useUserFactories";
import { fetchCurrentValues } from "@/core/api/influxdb";
import { DatapointSelectorDialog, type DatapointSelection } from "@/core/components/DatapointSelectorDialog";
import { ModuleStatsPopover } from "@/core/components/exposed-values/ModuleStatsPopover";
import { Card, CardContent } from "@/shared/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { StationDetailView } from "../components/StationDetailView";
import { OverviewTab } from "../components/OverviewTab";
import { DatasetsTab } from "../components/DatasetsTab";
import { LabelingTab } from "../components/LabelingTab";
import { TrainingTab } from "../components/TrainingTab";
import { DeploymentTab } from "../components/DeploymentTab";
import { ControlTab } from "../components/ControlTab";
import { AISettingsDialog } from "../components/AISettingsDialog";
import type {
  Area,
  Line,
  Station,
  FieldMapping,
  FactoryInfo,
  ExposedValueDefinition,
  ExposedValueMapping,
  LocationScope,
  RealTimeValue,
  AIEnabledStation,
} from "../types";
import {
  DEMO_FACTORY_ID,
  mockDatasets,
  mockAnomalies,
  mockControlActions,
  mockOverviewModels,
  mockTrainingJobs,
  mockTrainingHistory,
  getStationDeployment,
} from "../demo-data";
import {
  Database,
  Tag,
  Cpu,
  AlertTriangle,
  Settings,
  Activity,
  Zap,
  Target,
  Factory,
  FlaskConical,
  Rocket,
  Brain,
  Eye,
  RefreshCw,
  Loader2,
} from "lucide-react";

const getLocationKey = (mapping: FieldMapping) => {
  if (mapping.station_id) return `station:${mapping.station_id}`;
  if (mapping.line_id) return `line:${mapping.line_id}`;
  if (mapping.area_id) return `area:${mapping.area_id}`;
  return `factory:${mapping.factory_id}`;
};

const getScopeKey = (scope: LocationScope) => `${scope.type}:${scope.id}`;

const AIProductionControl = () => {
  const { selectedFactoryId, selectedFactory } = useFactory();
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { data: userFactories = [] } = useUserFactories();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
  const [selectedStation, setSelectedStation] = useState<AIEnabledStation | null>(null);
  const [realTimeValues, setRealTimeValues] = useState<Record<string, RealTimeValue>>({});
  const [isLoadingValues, setIsLoadingValues] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorContext, setSelectorContext] = useState<{
    scope: LocationScope;
    exposedValue: ExposedValueDefinition;
  } | null>(null);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [labelingDatasetId, setLabelingDatasetId] = useState<string | null>(null);

  const { sendLocalNotification } = usePushNotifications();

  // Determine if we should show mock data (Demo Factory only)
  const isDemoFactory = selectedFactoryId === DEMO_FACTORY_ID;
  const showMockData = isDemoFactory;

  // Fetch factory info for tenant context
  const { data: factoryInfo } = useQuery({
    queryKey: ["factory-details", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return null;
      const { data, error } = await supabase
        .schema("core")
        .from("factories")
        .select("id, tenant_id")
        .eq("id", selectedFactoryId)
        .maybeSingle();
      if (error) throw error;
      return data as FactoryInfo | null;
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch AI module from registry
  const { data: aiModule } = useQuery({
    queryKey: ["core", "module-registry", "ai"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("core")
        .from("module_registry")
        .select("id, name, display_name")
        .eq("name", "ai")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Fetch exposed value definitions for AI module
  const { data: exposedValues = [], isLoading: loadingExposedValues } = useQuery({
    queryKey: ["core", "exposed-values", aiModule?.id],
    queryFn: async () => {
      if (!aiModule?.id) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("exposed_value_definitions")
        .select("id, name, display_name, description, module_id")
        .eq("module_id", aiModule.id)
        .order("display_name");
      if (error) throw error;
      return data as ExposedValueDefinition[];
    },
    enabled: !!aiModule?.id,
  });

  // Fetch factory areas
  const { data: areas = [] } = useQuery({
    queryKey: ["factory-areas", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("factory_areas")
        .select("id, name, color, sort_order")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Area[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch production lines
  const { data: lines = [] } = useQuery({
    queryKey: ["production-lines", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("production_lines")
        .select("id, area_id, name, sort_order")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Line[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch stations
  const { data: stations = [] } = useQuery({
    queryKey: ["stations", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("stations")
        .select("id, line_id, name, station_type, sort_order")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch field mappings for factory
  const {
    data: fieldMappings = [],
    isLoading: loadingMappings,
    refetch: refetchMappings,
  } = useQuery({
    queryKey: ["field-mappings", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("field_mappings")
        .select(`
          id,
          factory_id,
          area_id,
          line_id,
          station_id,
          datapoint:datapoints (
            field_name,
            measurement_name,
            display_name,
            bucket_name,
            unit:units (symbol)
          )
        `)
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true);
      if (error) throw error;
      const mappings = (data as FieldMapping[]) || [];
      return mappings.filter((m) => m.datapoint);
    },
    enabled: !!selectedFactoryId,
  });

  const fieldMappingIds = useMemo(() => fieldMappings.map((m) => m.id), [fieldMappings]);
  const exposedValueIds = useMemo(() => exposedValues.map((v) => v.id), [exposedValues]);

  // Fetch exposed value mappings for AI module
  const { data: exposedValueMappings = [], refetch: refetchExposedValueMappings } = useQuery({
    queryKey: [
      "core",
      "exposed-value-mappings",
      selectedFactoryId,
      exposedValueIds.join(","),
      fieldMappingIds.join(","),
    ],
    queryFn: async () => {
      if (exposedValueIds.length === 0 || fieldMappingIds.length === 0 || !factoryInfo?.tenant_id) {
        return [];
      }
      const { data, error } = await supabase
        .schema("core")
        .from("datapoint_exposed_value_mapping")
        .select(`
          id,
          exposed_value_id,
          field_mapping_id,
          dynamic_key,
          exposed_value:exposed_value_definitions (
            id,
            name,
            display_name,
            description,
            module_id
          ),
          field_mapping:field_mappings (
            id,
            factory_id,
            area_id,
            line_id,
            station_id,
            datapoint:datapoints (
              display_name,
              field_name,
              measurement_name,
              bucket_name,
              unit:units (symbol)
            )
          )
        `)
        .eq("tenant_id", factoryInfo.tenant_id)
        .in("exposed_value_id", exposedValueIds)
        .in("field_mapping_id", fieldMappingIds);
      if (error) throw error;
      const mappings = (data as ExposedValueMapping[]) || [];
      return mappings.filter((m) => m.exposed_value && m.field_mapping);
    },
    enabled: exposedValueIds.length > 0 && fieldMappingIds.length > 0 && !!factoryInfo?.tenant_id,
  });

  // Build location-based mapping lookup
  const mappingByLocation = useMemo(() => {
    const map = new Map<string, Map<string, ExposedValueMapping>>();
    exposedValueMappings.forEach((mapping) => {
      if (!mapping.field_mapping) return;
      const locationKey = getLocationKey(mapping.field_mapping);
      const locationMap = map.get(locationKey) ?? new Map<string, ExposedValueMapping>();
      locationMap.set(mapping.exposed_value_id, mapping);
      map.set(locationKey, locationMap);
    });
    return map;
  }, [exposedValueMappings]);

  const getMappingForScope = useCallback(
    (scope: LocationScope, exposedValueId: string) => {
      return mappingByLocation.get(getScopeKey(scope))?.get(exposedValueId) ?? null;
    },
    [mappingByLocation]
  );

  const getFieldMappingsForScope = useCallback(
    (scope: LocationScope) => {
      switch (scope.type) {
        case "factory":
          return fieldMappings.filter((m) => !m.area_id && !m.line_id && !m.station_id);
        case "area":
          return fieldMappings.filter((m) => m.area_id === scope.id && !m.line_id && !m.station_id);
        case "line":
          return fieldMappings.filter((m) => m.line_id === scope.id && !m.station_id);
        case "station":
          return fieldMappings.filter((m) => m.station_id === scope.id);
        default:
          return [];
      }
    },
    [fieldMappings]
  );

  // Get all field mapping IDs that have exposed value mappings
  const mappedFieldMappingIds = useMemo(() => {
    return Array.from(new Set(exposedValueMappings.map((m) => m.field_mapping_id)));
  }, [exposedValueMappings]);

  // Fetch real-time values for mapped datapoints
  const fetchRealTimeValues = useCallback(async () => {
    if (mappedFieldMappingIds.length === 0) return;
    setIsLoadingValues(true);
    try {
      const data = await fetchCurrentValues(mappedFieldMappingIds);
      setRealTimeValues(data);
    } catch (error) {
      console.error("Error fetching real-time values:", error);
    } finally {
      setIsLoadingValues(false);
    }
  }, [mappedFieldMappingIds]);

  // Poll for real-time values
  useEffect(() => {
    if (mappedFieldMappingIds.length === 0) {
      setRealTimeValues((prev) => {
        // Only update if not already empty to avoid unnecessary re-renders
        if (Object.keys(prev).length === 0) return prev;
        return {};
      });
      return;
    }
    fetchRealTimeValues();
    const interval = setInterval(fetchRealTimeValues, 10000);
    return () => clearInterval(interval);
  }, [mappedFieldMappingIds, fetchRealTimeValues]);

  // Get stations with AI exposed value mappings
  const getStationMappingCount = useCallback(
    (stationId: string) => {
      const stationMappings = exposedValueMappings.filter(
        (m) => m.field_mapping?.station_id === stationId
      );
      return stationMappings.length;
    },
    [exposedValueMappings]
  );

  const stationHasAIMappings = useCallback(
    (stationId: string) => {
      return getStationMappingCount(stationId) > 0;
    },
    [getStationMappingCount]
  );

  // Get all AI-enabled stations with their area/line info
  const aiEnabledStations = useMemo<AIEnabledStation[]>(() => {
    return stations
      .filter((station) => stationHasAIMappings(station.id))
      .map((station) => {
        const line = lines.find((l) => l.id === station.line_id);
        const area = line ? areas.find((a) => a.id === line.area_id) : null;
        return {
          id: station.id,
          name: station.name,
          areaName: area?.name || "Unknown Area",
          lineName: line?.name || "Unknown Line",
          mappingCount: getStationMappingCount(station.id),
        };
      });
  }, [stations, lines, areas, stationHasAIMappings, getStationMappingCount]);

  // Stats for module popover - memoized to prevent unnecessary recalculations
  const { totalMappings, areaCount, lineCount, stationCount } = useMemo(() => {
    const total = exposedValueMappings.length;
    const locationKeys = Array.from(mappingByLocation.keys());
    const area = new Set(locationKeys.filter((k) => k.startsWith("area:"))).size;
    const line = new Set(locationKeys.filter((k) => k.startsWith("line:"))).size;
    const station = new Set(locationKeys.filter((k) => k.startsWith("station:"))).size;
    return {
      totalMappings: total,
      areaCount: area,
      lineCount: line,
      stationCount: station,
    };
  }, [exposedValueMappings, mappingByLocation]);

  // Check user permissions
  const canEditMappings = useMemo(() => {
    if (profile?.is_super_admin) return true;
    const roleName = userFactories
      .find((role) => role.factory_id === selectedFactoryId)
      ?.role?.toLowerCase();
    if (!roleName) return false;
    return roleName.includes("supervisor") || roleName.includes("manager") || roleName.includes("admin");
  }, [profile?.is_super_admin, selectedFactoryId, userFactories]);

  const hasDatapoints = fieldMappings.length > 0;
  const isMappingDisabled = !hasDatapoints || !canEditMappings;

  // Handle datapoint selection for exposed value mapping
  const handleSelectDatapoint = async (mapping: DatapointSelection) => {
    if (!selectorContext || !factoryInfo) return;
    if (!canEditMappings) {
      toast.error(t("mod_ai.ai_production.toast.permission", "You do not have access to edit mappings"));
      return;
    }
    setIsSavingMapping(true);
    try {
      const existingMapping = getMappingForScope(selectorContext.scope, selectorContext.exposedValue.id);
      if (existingMapping) {
        const { error: deleteError } = await supabase
          .schema("core")
          .from("datapoint_exposed_value_mapping")
          .delete()
          .eq("id", existingMapping.id);
        if (deleteError) throw deleteError;
      }
      const { error } = await supabase
        .schema("core")
        .from("datapoint_exposed_value_mapping")
        .insert({
          tenant_id: factoryInfo.tenant_id,
          field_mapping_id: mapping.id,
          exposed_value_id: selectorContext.exposedValue.id,
        });
      if (error) throw error;
      toast.success(t("mod_ai.ai_production.toast.mapped", "Exposed value mapped"));
      queryClient.invalidateQueries({
        queryKey: ["core", "exposed-value-mappings", selectedFactoryId],
      });
      setSelectorOpen(false);
    } catch (error) {
      toast.error(t("mod_ai.ai_production.toast.failed", "Failed to map exposed value"));
      console.error(error);
    } finally {
      setIsSavingMapping(false);
    }
  };

  const handleRefresh = () => {
    refetchMappings();
    refetchExposedValueMappings();
    fetchRealTimeValues();
  };

  const handleStationChange = (station: AIEnabledStation) => {
    setSelectedStation(station);
  };

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const toggleLine = (lineId: string) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const isLoading = loadingMappings || loadingExposedValues;

  if (!selectedFactoryId) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Card className="p-8 text-center">
          <Factory className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">
            {t("mod_ai.ai_production.empty.factory.title", "No Factory Selected")}
          </h2>
          <p className="text-muted-foreground">
            {t("mod_ai.ai_production.empty.factory.description", "Please select a factory to view AI Production Control")}
          </p>
        </Card>
      </div>
    );
  }

  // If a station is selected, show the detail view inline
  if (selectedStation && selectedFactoryId) {
    return (
      <StationDetailView
        stationId={selectedStation.id}
        stationName={selectedStation.name}
        areaName={selectedStation.areaName}
        lineName={selectedStation.lineName}
        factoryId={selectedFactoryId}
        onClose={() => setSelectedStation(null)}
        allStations={aiEnabledStations}
        onStationChange={handleStationChange}
      />
    );
  }

  const factoryScope: LocationScope = {
    type: "factory",
    id: selectedFactoryId,
    label: t("mod_ai.ai_production.level.factory", "Factory"),
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Demo Factory Banner */}
      {isDemoFactory && (
        <Card className="bg-gradient-to-r from-chart-2/10 to-chart-3/10 border-chart-2/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-chart-2" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {t("mod_ai.ai_production.demo.title", "Demo Factory - Sample Data")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "mod_ai.ai_production.demo.description",
                    "This factory displays sample AI production control data for demonstration purposes"
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Brain className="h-8 w-8 text-primary" />
            {t("mod_ai.ai_production.title", "AI Production Control")}
            <ModuleStatsPopover
              totalMappings={totalMappings}
              areaCount={areaCount}
              lineCount={lineCount}
              stationCount={stationCount}
              isLoading={isLoading}
              iconColorClass="text-primary"
            />
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t("mod_ai.ai_production.subtitle", "Zero-defect production through intelligent anomaly detection")}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          {isLoadingValues && (
            <Badge variant="secondary" className="gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t("mod_ai.ai_production.updating", "Updating...")}
            </Badge>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-primary/10 rounded-lg border border-primary/20">
            <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs md:text-sm font-medium">
              {t("mod_ai.ai_production.status.active", "Active")}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingValues ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t("mod_ai.ai_production.refresh", "Refresh")}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("mod_ai.ai_production.settings", "Settings")}</span>
          </Button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {t("mod_ai.ai_production.kpi.stations", "AI Stations")}
                </p>
                <p className="text-2xl md:text-3xl font-bold">{aiEnabledStations.length}</p>
              </div>
              <Brain className="h-8 w-8 md:h-10 md:w-10 text-primary opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1 md:mt-2 hidden sm:block">
              {t("mod_ai.ai_production.kpi.stations_desc", "With mapped values")}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-success/5 to-success/10 border-success/20">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {t("mod_ai.ai_production.kpi.mappings", "Mappings")}
                </p>
                <p className="text-2xl md:text-3xl font-bold text-success">{totalMappings}</p>
              </div>
              <Target className="h-8 w-8 md:h-10 md:w-10 text-success opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1 md:mt-2 hidden sm:block">
              {t("mod_ai.ai_production.kpi.mappings_desc", "Exposed values configured")}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-warning/5 to-warning/10 border-warning/20">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {t("mod_ai.ai_production.kpi.definitions", "Value Types")}
                </p>
                <p className="text-2xl md:text-3xl font-bold text-warning">{exposedValues.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 md:h-10 md:w-10 text-warning opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1 md:mt-2 hidden sm:block">
              {t("mod_ai.ai_production.kpi.definitions_desc", "Available to map")}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-chart-2/5 to-chart-2/10 border-chart-2/20">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {t("mod_ai.ai_production.kpi.datapoints", "Datapoints")}
                </p>
                <p className="text-2xl md:text-3xl font-bold text-chart-2">{fieldMappings.length}</p>
              </div>
              <Zap className="h-8 w-8 md:h-10 md:w-10 text-chart-2 opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1 md:mt-2 hidden sm:block">
              {t("mod_ai.ai_production.kpi.datapoints_desc", "Available to assign")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-auto min-w-full md:min-w-0">
            <TabsTrigger value="overview" className="gap-1.5 text-xs md:text-sm px-2 md:px-3">
              <Activity className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden xs:inline">{t("mod_ai.ai_production.tabs.overview", "Overview")}</span>
            </TabsTrigger>
            <TabsTrigger value="datasets" className="gap-1.5 text-xs md:text-sm px-2 md:px-3">
              <Database className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden xs:inline">{t("mod_ai.ai_production.tabs.datasets", "Datasets")}</span>
            </TabsTrigger>
            <TabsTrigger value="labeling" className="gap-1.5 text-xs md:text-sm px-2 md:px-3">
              <Tag className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden xs:inline">{t("mod_ai.ai_production.tabs.labeling", "Labeling")}</span>
            </TabsTrigger>
            <TabsTrigger value="training" className="gap-1.5 text-xs md:text-sm px-2 md:px-3">
              <FlaskConical className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden xs:inline">{t("mod_ai.ai_production.tabs.training", "Training")}</span>
            </TabsTrigger>
            <TabsTrigger value="deployment" className="gap-1.5 text-xs md:text-sm px-2 md:px-3">
              <Rocket className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden xs:inline">{t("mod_ai.ai_production.tabs.deploy", "Deploy")}</span>
            </TabsTrigger>
            <TabsTrigger value="control" className="gap-1.5 text-xs md:text-sm px-2 md:px-3">
              <Cpu className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden xs:inline">{t("mod_ai.ai_production.tabs.control", "Control")}</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            showMockData={showMockData}
            anomalies={mockAnomalies}
            controlActions={mockControlActions}
            models={mockOverviewModels}
          />
        </TabsContent>

        {/* Datasets Tab */}
        <TabsContent value="datasets" className="space-y-4">
          <DatasetsTab
            showMockData={showMockData}
            datasets={mockDatasets}
            onViewDataset={(datasetId) => {
              setLabelingDatasetId(datasetId);
              setActiveTab("labeling");
            }}
          />
        </TabsContent>

        {/* Labeling Tab */}
        <TabsContent value="labeling" className="space-y-4">
          <LabelingTab
            showMockData={showMockData}
            datasets={mockDatasets}
            initialDatasetId={labelingDatasetId}
            onDatasetCleared={() => setLabelingDatasetId(null)}
          />
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-4">
          <TrainingTab
            showMockData={showMockData}
            trainingJobs={mockTrainingJobs}
            trainingHistory={mockTrainingHistory}
          />
        </TabsContent>

        {/* Deployment Tab */}
        <TabsContent value="deployment" className="space-y-4">
          <DeploymentTab
            showMockData={showMockData}
            areas={areas}
            lines={lines}
            stations={stations}
            expandedAreas={expandedAreas}
            expandedLines={expandedLines}
            toggleArea={toggleArea}
            toggleLine={toggleLine}
            getStationDeployment={getStationDeployment}
            stationHasAIMappings={stationHasAIMappings}
            getStationMappingCount={getStationMappingCount}
            setSelectedStation={(station) => {
              if (station) {
                const line = lines.find((l) => l.id === station.line_id);
                const area = line ? areas.find((a) => a.id === line.area_id) : null;
                setSelectedStation({
                  id: station.id,
                  name: station.name,
                  areaName: area?.name || "Unknown Area",
                  lineName: line?.name || "Unknown Line",
                  mappingCount: getStationMappingCount(station.id),
                });
              }
            }}
          />
        </TabsContent>

        {/* Control Tab */}
        <TabsContent value="control" className="space-y-4">
          <ControlTab showMockData={showMockData} mockControlActions={mockControlActions} />
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <AISettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        exposedValues={exposedValues}
        factoryScope={factoryScope}
        areas={areas}
        lines={lines}
        stations={stations}
        hasDatapoints={hasDatapoints && canEditMappings}
        realTimeValues={realTimeValues}
        getMappingForScope={getMappingForScope}
        onSelectMapping={(scope, definition) => {
          setSelectorContext({ scope, exposedValue: definition });
          setSelectorOpen(true);
        }}
        onOpenChart={() => {
          // Chart functionality can be added here
        }}
      />

      {/* Datapoint Selector Dialog */}
      <DatapointSelectorDialog
        open={selectorOpen}
        onOpenChange={(open) => {
          setSelectorOpen(open);
          if (!open) setSelectorContext(null);
        }}
        scopedMappings={selectorContext ? getFieldMappingsForScope(selectorContext.scope) : []}
        allMappings={fieldMappings}
        selectedId={
          selectorContext
            ? getMappingForScope(selectorContext.scope, selectorContext.exposedValue.id)?.field_mapping_id ?? null
            : null
        }
        onSelect={handleSelectDatapoint}
        title={
          selectorContext
            ? t(
                "mod_ai.ai_production.selector.title",
                `Select datapoint for ${selectorContext.exposedValue.display_name}`
              )
            : t("mod_ai.ai_production.selector.titleFallback", "Select datapoint")
        }
        description={
          selectorContext
            ? t(
                "mod_ai.ai_production.selector.description",
                `Assign a datapoint at ${selectorContext.scope.label} level.`
              )
            : t("mod_ai.ai_production.selector.descriptionFallback", "Choose an existing datapoint mapping.")
        }
      />

      {isSavingMapping && (
        <div className="fixed bottom-6 right-6">
          <Badge variant="secondary" className="gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("mod_ai.ai_production.saving", "Saving mapping...")}
          </Badge>
        </div>
      )}
    </div>
  );
};

export default AIProductionControl;
